<script setup>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import KanbanColumn from './KanbanColumn.vue';
import AddLabel from '../settings/labels/AddLabel.vue';
import EditLabel from '../settings/labels/EditLabel.vue';

import draggable from 'vuedraggable';
import ConversationApi from 'dashboard/api/inbox/conversation';

const store = useStore();

const accountLabels = useMapGetter('labels/getLabels');
const allConversations = useMapGetter('getAllConversations');

const showAddLabelModal = ref(false);
const showEditLabelModal = ref(false);
const selectedLabel = ref(null);
const selectedExistingLabel = ref('');
const selectedAssignee = ref('');
const selectedBoard = ref('default');
const sortBy = ref('last_activity_desc');
const searchQuery = ref('');
const leadsLimit = ref(50);
const isSyncing = ref(false);

const apiColumns = ref({}); // Store API results per label title

const openDropdown = ref('');
const toggleDropdown = (name) => {
  if (openDropdown.value === name) openDropdown.value = '';
  else openDropdown.value = name;
};

const agents = useMapGetter('agents/getAgents');

const boards = computed(() => {
  if (!accountLabels.value) return ['default'];
  const boardNames = new Set(['default']);
  accountLabels.value.forEach(label => {
    const desc = label.description || '';
    if (desc.includes('[kanban=true]')) return;
    const match = desc.match(/\[kanban:([^:]+):\d+\]/);
    if (match) boardNames.add(match[1]);
  });
  return Array.from(boardNames);
});

const handleBoardChange = (action) => {
  if (action === 'new') {
    const name = prompt('Qual o nome do novo quadro?');
    if (name) {
      selectedBoard.value = name;
    }
  }
};

const editBoardName = (oldName) => {
  const newName = prompt('Novo nome para o quadro:', oldName);
  if (!newName || newName === oldName || newName === 'default') return;
  
  const labelsToUpdate = accountLabels.value.filter(label => {
    return new RegExp(`\\[kanban:${oldName}:\\d+\\]`).test(label.description || '');
  });
  
  labelsToUpdate.forEach(label => {
    const regex = new RegExp(`\\[kanban:${oldName}:(\\d+)\\]`, 'g');
    const newDesc = label.description.replace(regex, `[kanban:${newName}:$1]`);
    store.dispatch('labels/update', {
      id: label.id,
      color: label.color,
      description: newDesc,
      title: label.title,
      show_on_sidebar: label.show_on_sidebar
    });
  });
  selectedBoard.value = newName;
};

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('agents/get');
  loadMoreLeads();
  
  document.addEventListener('click', (e) => {
    if (!e.target.closest('.kanban-dropdown')) {
      openDropdown.value = '';
    }
  });
});

const loadMoreLeads = async () => {
  isSyncing.value = true;
  store.dispatch('fetchAllConversations'); // keeping local store fresh
  
  if (kanbanLabels.value.length > 0) {
    const promises = kanbanLabels.value.map(async (label) => {
      try {
        const { data } = await ConversationApi.get({
          labels: [label.title],
          status: 'all',
          page: 1,
          sortBy: sortBy.value,
          assigneeType: selectedAssignee.value === 'unassigned' ? 'unassigned' : 'all',
        });
        
        apiColumns.value[label.title] = {
          items: data?.data?.payload || [],
          meta: data?.data?.meta || {}
        };
      } catch(e) {
        // ignore individual failures
      }
    });
    await Promise.all(promises);
  }
  
  isSyncing.value = false;
};

const kanbanLabels = computed(() => {
  if (!accountLabels.value) return [];
  const labels = accountLabels.value.filter(label => {
    const desc = label.description || '';
    if (selectedBoard.value === 'default') {
      return desc.includes('[kanban=true]') || (/\[kanban:\d+\]/.test(desc));
    } else {
      return new RegExp(`\\[kanban:${selectedBoard.value}:\\d+\\]`).test(desc);
    }
  });
  
  labels.sort((a, b) => {
    const getOrder = (desc) => {
      if (selectedBoard.value === 'default') {
        const match = desc.match(/\[kanban:(\d+)\]/);
        return match ? parseInt(match[1], 10) : 999;
      } else {
        const regex = new RegExp(`\\[kanban:${selectedBoard.value}:(\\d+)\\]`);
        const match = desc.match(regex);
        return match ? parseInt(match[1], 10) : 999;
      }
    };
    return getOrder(a.description || '') - getOrder(b.description || '');
  });
  
  return labels;
});

const availableLabels = computed(() => {
  if (!accountLabels.value) return [];
  const currentKanbanTitles = kanbanLabels.value.map(l => l.title);
  return accountLabels.value.filter(l => !currentKanbanTitles.includes(l.title));
});

const addExistingLabelToKanban = () => {
  if (!selectedExistingLabel.value) return;
  const label = accountLabels.value.find(l => l.title === selectedExistingLabel.value);
  if (label) {
    const newOrder = kanbanLabels.value.length + 1;
    let desc = label.description || '';
    if (selectedBoard.value === 'default') {
      desc += ` [kanban:${newOrder}]`;
    } else {
      desc += ` [kanban:${selectedBoard.value}:${newOrder}]`;
    }
    
    store.dispatch('labels/update', {
      id: label.id,
      color: label.color,
      description: desc.trim(),
      title: label.title,
      show_on_sidebar: label.show_on_sidebar
    });
  }
  selectedExistingLabel.value = '';
};

const columnsData = computed({
  get() {
    const cols = kanbanLabels.value.map(label => {
      return {
        label: label,
        items: [],
        apiMeta: apiColumns.value[label.title]?.meta || {},
        isDefault: false
      }
    });
    
    // Merge API fetched chats with Vuex chats to ensure reactivity
    let allChatsMap = new Map();
    
    // 1. Add API chats
    Object.values(apiColumns.value).forEach(colData => {
      (colData.items || []).forEach(chat => {
        allChatsMap.set(chat.id, chat);
      });
    });
    
    // 2. Override/Add Vuex chats
    allConversations.value.forEach(chat => {
      allChatsMap.set(chat.id, chat);
    });
    
    let sortedChats = Array.from(allChatsMap.values())
      .filter(chat => {
        if (selectedAssignee.value && selectedAssignee.value !== 'unassigned') {
          if (chat.meta?.assignee?.id !== parseInt(selectedAssignee.value)) return false;
        } else if (selectedAssignee.value === 'unassigned') {
          if (chat.meta?.assignee?.id) return false;
        }
        if (searchQuery.value) {
          const name = chat.meta?.sender?.name || '';
          if (!name.toLowerCase().includes(searchQuery.value.toLowerCase())) return false;
        }
        return true;
      });
      
    // Deduplicate by Contact ID
    const uniqueChats = [];
    const seenContacts = new Set();

    sortedChats.forEach(chat => {
      const contactId = chat.meta?.sender?.id;
      if (!contactId || !seenContacts.has(contactId)) {
        uniqueChats.push(chat);
        if (contactId) seenContacts.add(contactId);
      }
    });
    
    uniqueChats.forEach(chat => {
      const chatKanbanLabels = kanbanLabels.value.filter(l => chat.labels && chat.labels.includes(l.title));
      if (chatKanbanLabels.length > 0) {
        const targetCol = cols.find(c => c.label.title === chatKanbanLabels[0].title);
        if (targetCol) {
          targetCol.items.push(chat);
        }
      }
    });
    
    return cols;
  },
  set(newCols) {
    newCols.forEach((col, index) => {
      const newOrder = index + 1;
      const label = col.label;
      let desc = (label.description || '').replace(/\[kanban.*?\]/g, '').trim();
      if (selectedBoard.value === 'default') {
        desc += ` [kanban:${newOrder}]`;
      } else {
        desc += ` [kanban:${selectedBoard.value}:${newOrder}]`;
      }
      
      store.dispatch('labels/update', {
        id: label.id,
        color: label.color,
        description: desc.trim(),
        title: label.title,
        show_on_sidebar: label.show_on_sidebar
      });
    });
  }
});

const openAddLabelModal = () => {
  showAddLabelModal.value = true;
};

const openEditLabelModal = (label) => {
  selectedLabel.value = label;
  showEditLabelModal.value = true;
};

</script>

<template>
  <div class="h-full flex flex-col w-full overflow-hidden bg-n-background">
    <!-- Top Options Row -->
    <div class="flex flex-nowrap items-center justify-between w-full h-[3.25rem] px-4 border-b border-n-strong bg-n-surface-1 shrink-0 gap-4 overflow-visible relative z-[20]">
      
      <!-- Search Bar Native Style -->
      <div class="flex items-center gap-2 px-2 py-1 h-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color focus-within:bg-n-surface-1 focus-within:shadow-sm transition-all duration-100 ease-out focus-within:outline-n-brand-5 min-w-[200px] w-full max-w-sm shrink-0">
        <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10"></span>
        <input 
          type="search" 
          v-model="searchQuery"
          class="reset-base outline-none w-full m-0 bg-transparent border-transparent shadow-none text-n-slate-12 active:border-transparent hover:border-transparent focus:border-transparent placeholder:text-n-slate-10 text-sm font-medium" 
          placeholder="Pesquisar leads (Filtro local)..."
        >
        <span v-if="searchQuery" class="text-[10px] whitespace-nowrap text-n-slate-9 italic pr-1 select-none pointer-events-none">Filtrando...</span>
      </div>

      <!-- Filters & Actions -->
      <div class="flex items-center gap-2 shrink-0">
        <!-- Leads Limit Dropdown -->
        <div class="relative kanban-dropdown shrink-0 group">
          <button type="button" @click="toggleDropdown('limit')" class="relative flex h-8 items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-all cursor-pointer border-none bg-transparent">
            <div class="inline-flex items-center gap-2 min-w-0">
              <span class="i-lucide-hash size-4 flex-shrink-0"></span>
              <span class="flex-grow truncate text-start text-sm font-medium leading-5">{{ leadsLimit }} leads</span>
            </div>
          </button>
          <div class="absolute right-0 top-full mt-1 w-40 bg-n-surface-1 border border-n-strong rounded-lg shadow-lg z-50 py-1 flex flex-col transition-all duration-150 group-hover:opacity-100 group-hover:visible" :class="openDropdown === 'limit' ? 'opacity-100 visible' : 'opacity-0 invisible'">
            <button v-for="val in [25, 50, 100, 250]" :key="val" @click="leadsLimit = val; openDropdown = ''" class="flex items-center px-3 py-1.5 text-sm text-n-slate-11 hover:text-n-slate-12 ltr:hover:bg-gradient-to-r rtl:hover:bg-gradient-to-l from-transparent via-n-slate-3/70 to-n-slate-3/70 cursor-pointer border-none bg-transparent w-full text-left">
              {{ val }} leads
            </button>
          </div>
        </div>

        <!-- Sort By Dropdown -->
        <div class="relative kanban-dropdown shrink-0 group">
          <button type="button" @click="toggleDropdown('sort')" class="relative flex h-8 items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-all cursor-pointer border-none bg-transparent">
            <div class="inline-flex items-center gap-2 min-w-0">
              <span class="i-lucide-arrow-up-down size-4 flex-shrink-0"></span>
              <span class="flex-grow truncate text-start text-sm font-medium leading-5">
                {{ sortBy === 'last_activity_desc' ? 'Mensagem + Recente' : sortBy === 'last_activity_asc' ? 'Mensagem + Antiga' : sortBy === 'time_in_column_desc' ? 'Mais tempo na Etapa' : 'Menos tempo na Etapa' }}
              </span>
            </div>
          </button>
          <div class="absolute right-0 top-full mt-1 w-56 bg-n-surface-1 border border-n-strong rounded-lg shadow-lg z-50 py-1 flex flex-col transition-all duration-150 group-hover:opacity-100 group-hover:visible" :class="openDropdown === 'sort' ? 'opacity-100 visible' : 'opacity-0 invisible'">
            <button v-for="opt in [{val:'last_activity_desc', text:'Mensagem + Recente'}, {val:'last_activity_asc', text:'Mensagem + Antiga'}, {val:'time_in_column_desc', text:'Mais tempo na Etapa'}, {val:'time_in_column_asc', text:'Menos tempo na Etapa'}]" :key="opt.val" @click="sortBy = opt.val; openDropdown = ''" class="flex items-center px-3 py-1.5 text-sm text-n-slate-11 hover:text-n-slate-12 ltr:hover:bg-gradient-to-r rtl:hover:bg-gradient-to-l from-transparent via-n-slate-3/70 to-n-slate-3/70 cursor-pointer border-none bg-transparent w-full text-left">
              {{ opt.text }}
            </button>
          </div>
        </div>
        
        <!-- Assignee Dropdown -->
        <div class="relative kanban-dropdown shrink-0 group">
          <button type="button" @click="toggleDropdown('assignee')" class="relative flex h-8 items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-all cursor-pointer border-none bg-transparent">
            <div class="inline-flex items-center gap-2 min-w-0">
              <span class="i-lucide-users size-4 flex-shrink-0"></span>
              <span class="flex-grow truncate text-start text-sm font-medium leading-5">
                {{ !selectedAssignee ? 'Todos Atendentes' : selectedAssignee === 'unassigned' ? 'Não Atribuídos' : agents.find(a => a.id === selectedAssignee)?.name || 'Todos Atendentes' }}
              </span>
            </div>
          </button>
          <div class="absolute right-0 top-full mt-1 w-48 max-h-60 overflow-y-auto bg-n-surface-1 border border-n-strong rounded-lg shadow-lg z-50 py-1 flex flex-col transition-all duration-150 group-hover:opacity-100 group-hover:visible" :class="openDropdown === 'assignee' ? 'opacity-100 visible' : 'opacity-0 invisible'">
            <button @click="selectedAssignee = ''; openDropdown = ''" class="flex items-center px-3 py-1.5 text-sm text-n-slate-11 hover:text-n-slate-12 ltr:hover:bg-gradient-to-r rtl:hover:bg-gradient-to-l from-transparent via-n-slate-3/70 to-n-slate-3/70 cursor-pointer border-none bg-transparent w-full text-left">Todos Atendentes</button>
            <button @click="selectedAssignee = 'unassigned'; openDropdown = ''" class="flex items-center px-3 py-1.5 text-sm text-n-slate-11 hover:text-n-slate-12 ltr:hover:bg-gradient-to-r rtl:hover:bg-gradient-to-l from-transparent via-n-slate-3/70 to-n-slate-3/70 cursor-pointer border-none bg-transparent w-full text-left">Não Atribuídos</button>
            <button v-for="agent in agents" :key="agent.id" @click="selectedAssignee = agent.id; openDropdown = ''" class="flex items-center px-3 py-1.5 text-sm text-n-slate-11 hover:text-n-slate-12 ltr:hover:bg-gradient-to-r rtl:hover:bg-gradient-to-l from-transparent via-n-slate-3/70 to-n-slate-3/70 cursor-pointer border-none bg-transparent w-full text-left truncate">
              {{ agent.name }}
            </button>
          </div>
        </div>
        
        <!-- Existing Label Dropdown -->
        <div class="relative kanban-dropdown shrink-0 group">
          <button type="button" @click="toggleDropdown('label')" class="relative flex h-8 items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-all cursor-pointer border-none bg-transparent">
            <div class="inline-flex items-center gap-2 min-w-0">
              <span class="i-lucide-tag size-4 flex-shrink-0"></span>
              <span class="flex-grow truncate text-start text-sm font-medium leading-5">+ Etiqueta</span>
            </div>
          </button>
          <div class="absolute right-0 top-full mt-1 w-56 max-h-60 overflow-y-auto bg-n-surface-1 border border-n-strong rounded-lg shadow-lg z-50 py-1 flex flex-col transition-all duration-150 group-hover:opacity-100 group-hover:visible" :class="openDropdown === 'label' ? 'opacity-100 visible' : 'opacity-0 invisible'">
            <button v-for="label in availableLabels" :key="label.id" @click="selectedExistingLabel = label.title; addExistingLabelToKanban(); openDropdown = ''" class="flex items-center px-3 py-1.5 text-sm text-n-slate-11 hover:text-n-slate-12 ltr:hover:bg-gradient-to-r rtl:hover:bg-gradient-to-l from-transparent via-n-slate-3/70 to-n-slate-3/70 cursor-pointer border-none bg-transparent w-full text-left">
              {{ label.title }}
            </button>
            <div v-if="availableLabels.length === 0" class="px-3 py-2 text-sm text-n-slate-9 italic">Nenhuma etiqueta...</div>
          </div>
        </div>

        <button @click="openAddLabelModal" class="relative flex h-8 items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-all cursor-pointer border-none bg-transparent shrink-0">
          <div class="inline-flex items-center gap-2 min-w-0">
            <span class="i-lucide-plus size-4 flex-shrink-0"></span>
            <span class="flex-grow truncate text-start text-sm font-medium leading-5">Nova Etapa</span>
          </div>
        </button>

        <button @click="loadMoreLeads" :disabled="isSyncing" class="relative flex h-8 items-center justify-center rounded-lg px-2 py-1.5 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-all cursor-pointer border-none bg-transparent shrink-0" title="Sincronizar">
          <span class="i-lucide-refresh-cw size-4" :class="{ 'animate-spin': isSyncing }"></span>
        </button>
      </div>
    </div>

    <!-- Board Selector Tabs -->
    <ul class="flex items-center border-b border-n-slate-3 bg-n-surface-1 px-4 overflow-x-auto shrink-0 m-0 list-none gap-2 min-h-[40px] relative z-[10]">
      <li 
        v-for="b in boards" 
        :key="b" 
        class="flex items-center gap-2 px-3 py-2 cursor-pointer border-b-2 transition-colors whitespace-nowrap group"
        :class="selectedBoard === b ? 'border-n-brand-5 text-n-brand-5' : 'border-transparent text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2'"
        @click="selectedBoard = b"
      >
        <span class="i-lucide-kanban-square size-4"></span>
        <span class="text-sm font-medium">{{ b === 'default' ? 'Kanban Principal' : b }}</span>
        <button 
          v-if="b !== 'default' && selectedBoard === b" 
          class="opacity-0 group-hover:opacity-100 transition-opacity i-lucide-pencil size-3.5 ml-1 text-n-slate-10 hover:text-n-brand-5 border-none bg-transparent cursor-pointer" 
          title="Editar nome do quadro"
          @click.stop="editBoardName(b)"
        ></button>
      </li>
      <li 
        class="flex items-center gap-2 px-3 py-2 cursor-pointer border-b-2 border-transparent text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 whitespace-nowrap" 
        @click="handleBoardChange('new')"
      >
        <span class="i-lucide-plus size-4"></span>
        <span class="text-sm font-medium">Criar Quadro</span>
      </li>
    </ul>
    
    <div class="flex-1 min-h-0 w-full overflow-auto bg-n-background relative kanban-scroll-container">
      <draggable 
        v-model="columnsData"
        group="kanban-columns"
        item-key="label.id"
        handle=".column-drag-handle"
        :delay="100"
        :delayOnTouchOnly="true"
        class="flex flex-nowrap p-4 gap-4 items-start h-full min-w-max pb-8"
        ghost-class="opacity-50"
      >
        <template #item="{element}">
          <KanbanColumn 
            :column="element"
            :all-kanban-labels="kanbanLabels"
            @edit-column="openEditLabelModal"
          />
        </template>
      </draggable>
    </div>

    <!-- Modals para criar e editar etiquetas sem sair do Kanban -->
    <woot-modal :show.sync="showAddLabelModal" :on-close="() => (showAddLabelModal = false)">
      <AddLabel @close="showAddLabelModal = false" />
    </woot-modal>

    <woot-modal :show.sync="showEditLabelModal" :on-close="() => (showEditLabelModal = false)">
      <EditLabel v-if="selectedLabel" :selected-response="selectedLabel" @close="showEditLabelModal = false" />
    </woot-modal>
  </div>
</template>

<style scoped>
/* Scrollbar customizada para o Kanban */
::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background-color: var(--s-300);
  border-radius: 20px;
}
<style scoped>
.kanban-scroll-container::-webkit-scrollbar {
  height: 12px;
  width: 12px;
}
.kanban-scroll-container::-webkit-scrollbar-track {
  background: transparent;
}
.kanban-scroll-container::-webkit-scrollbar-thumb {
  background-color: rgba(148, 163, 184, 0.4);
  border-radius: 6px;
  border: 3px solid transparent;
  background-clip: content-box;
}
.kanban-scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(148, 163, 184, 0.6);
}
</style>
